<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { routes } from '@app/router/routes'
import { formatToInternational } from '@shared/utils'
import { usePortfolioHoldingsSummaryQuery } from '@modules/portfolio'
import TokenWalletTab from '../components/TokenWalletTab.vue'
import { useCryptoWalletQuery } from '../api/queries/useCryptoWalletQuery'
import { useCryptoTransactionsQuery } from '../api/queries/useCryptoTransactionsQuery'
import type { CryptoTransactionFilters } from '../types/cryptoWalletTypes'
import { UNFILTERED_CRYPTO_TRANSACTIONS } from '../constants/cryptoTransactionConstants'
import type { Transaction } from '../../transactions/types/transactionTypes'

const router = useRouter()

// The wallet record carries the XRPL address + explorer link; the token COUNT
// is portfolio data (`holdings/summary`), because the crypto wallet API does
// not return one. Two sources, one screen.
const walletQuery = useCryptoWalletQuery()
const holdingsQuery = usePortfolioHoldingsSummaryQuery()

const RECENT_FILTERS: CryptoTransactionFilters = { ...UNFILTERED_CRYPTO_TRANSACTIONS }
const transactionsQuery = useCryptoTransactionsQuery(() => RECENT_FILTERS)

const wallet = computed(() => walletQuery.data.value ?? null)
const explorerUrl = computed(() => wallet.value?.explorerUrl ?? '')

const tokensHeld = computed(() => holdingsQuery.data.value?.totalTokensHeld ?? 0)
const totalTokens = computed(() => formatToInternational(tokensHeld.value))

const transactions = computed(
  () => transactionsQuery.data.value?.pages.flatMap((page) => page.rows) ?? [],
)
const loadingMore = computed(() => transactionsQuery.isFetchingNextPage.value)
const hasMore = computed(() => Boolean(transactionsQuery.hasNextPage.value))

const isLoading = computed(
  () => holdingsQuery.isLoading.value || transactionsQuery.isLoading.value,
)

// Only claim "no tokens" once both sources have answered and both are empty. A
// failed or in-flight request renders the populated shell instead, so a network
// blip never tells an invested user they hold nothing.
const hasError = computed(() => holdingsQuery.isError.value || transactionsQuery.isError.value)

const isEmpty = computed(
  () =>
    !isLoading.value &&
    !hasError.value &&
    tokensHeld.value === 0 &&
    transactions.value.length === 0,
)

const onLoadMore = () => {
  if (transactionsQuery.hasNextPage.value && !transactionsQuery.isFetchingNextPage.value) {
    transactionsQuery.fetchNextPage()
  }
}

// `source=token` tells the shared detail screen to resolve the row from the
// crypto feed rather than the fiat activity feed.
const onViewDetails = (tx: Transaction) => {
  router.push({
    path: routes.WALLET_TRANSACTION_DETAIL.replace(':id', tx.id),
    query: { source: 'token' },
  })
}
</script>

<template>
  <TokenWalletTab
    :total-tokens="totalTokens"
    :explorer-url="explorerUrl"
    :transactions="transactions"
    :loading="isLoading"
    :loading-more="loadingMore"
    :has-more="hasMore"
    :is-empty="isEmpty"
    :see-all-to="routes.WALLET_TOKEN_TRANSACTIONS"
    @load-more="onLoadMore"
    @view-details="onViewDetails"
  />
</template>
